import React, { useState, useEffect } from 'react';
import { User, Role, UserFormData, UserFormErrors, ModulePermission } from '../../types';
import { userApi } from '../../services/userApi';
import ModulePermissionSelector from '../common/ModulePermissionSelector';

interface UserFormProps {
  user?: User | null;
  roles: Role[];
  onClose: () => void;
  onSuccess: () => void;
}

const UserForm: React.FC<UserFormProps> = ({ user, roles, onClose, onSuccess }) => {
  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    permissions: [],
    isActive: true,
  });
  const [errors, setErrors] = useState<UserFormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const isEditing = !!user;

  useEffect(() => {
    if (user) {
      // Convert user permissions to ModulePermission format
      const userPermissions: ModulePermission[] = user.permissions?.map(p => ({
        moduleName: p.module_name,
        displayName: p.module_name.split('-').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' '),
        canCreate: p.can_create,
        canRead: p.can_read,
        canUpdate: p.can_update,
        canDelete: p.can_delete
      })) || [];

      setFormData({
        email: user.email,
        firstName: user.first_name,
        lastName: user.last_name,
        permissions: userPermissions,
        isActive: Boolean(user.is_active), // Ensure it's a proper boolean
      });
    }
  }, [user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    
    // Clear error when user starts typing
    if (errors[name as keyof UserFormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: UserFormErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!isEditing && !formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password && formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    } else if (formData.password && !/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])/.test(formData.password)) {
      newErrors.password = 'Password must contain uppercase, lowercase, number, and special character';
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.permissions || formData.permissions.length === 0) {
      newErrors.permissions = 'At least one module permission is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      const submitData = {
        ...formData,
        permissions: formData.permissions || [],
      };

      // Remove password from update if not provided
      if (isEditing && !formData.password) {
        delete submitData.password;
      }

      let response;
      if (isEditing && user) {
        response = await userApi.updateUser(user.id, submitData);
      } else {
        response = await userApi.createUser(submitData);
      }

      if (response.success) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Form submission error:', err);
      const errorMessage = err?.message || err?.error || '';
      if (errorMessage.includes('Email already exists')) {
        setErrors({ email: 'Email already exists' });
      } else if (errorMessage.includes('Password does not meet requirements')) {
        setErrors({ password: 'Password does not meet security requirements' });
      } else {
        setErrors({ general: errorMessage || 'Failed to save user' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditing ? 'Edit User' : 'Create New User'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="user-form">
          {errors.general && (
            <div className="error-message general-error">
              {errors.general}
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First Name *</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className={errors.firstName ? 'error' : ''}
                disabled={isLoading}
              />
              {errors.firstName && (
                <span className="error-message">{errors.firstName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="lastName">Last Name *</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className={errors.lastName ? 'error' : ''}
                disabled={isLoading}
              />
              {errors.lastName && (
                <span className="error-message">{errors.lastName}</span>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={errors.email ? 'error' : ''}
              disabled={isLoading}
            />
            {errors.email && (
              <span className="error-message">{errors.email}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="password">
              Password {!isEditing && '*'}
              {isEditing && <small>(leave blank to keep current password)</small>}
            </label>
            <div className="password-input-container">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password || ''}
                onChange={handleInputChange}
                className={errors.password ? 'error' : ''}
                disabled={isLoading}
                placeholder={isEditing ? 'Enter new password (optional)' : 'Enter password'}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            {errors.password && (
              <span className="error-message">{errors.password}</span>
            )}
          </div>

          <div className="form-group">
            <ModulePermissionSelector
              selectedPermissions={formData.permissions || []}
              onChange={(permissions) => {
                setFormData(prev => ({ ...prev, permissions }));
                // Clear error when permissions are selected
                if (errors.permissions) {
                  setErrors(prev => ({ ...prev, permissions: '' }));
                }
              }}
              disabled={isLoading || (user?.is_superadmin === true)}
            />
            {errors.permissions && (
              <span className="error-message">{errors.permissions}</span>
            )}
            {user?.is_superadmin === true && (
              <small className="form-note">Superadmin permissions cannot be changed</small>
            )}
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleInputChange}
                disabled={isLoading || (user?.is_superadmin === true)}
              />
              Active User
            </label>
            {user?.is_superadmin === true && (
              <small className="form-note">Superadmin cannot be deactivated</small>
            )}
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : (isEditing ? 'Update User' : 'Create User')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserForm;